import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Upload, 
  X, 
  Search, 
  Sparkles,
  Image as ImageIcon
} from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { uploadMediaFile, isImageFile } from '../../utils/uploadHelper';

const TeamManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadingImage, setUploadingImage] = useState(false);

  const availableCategories = [
    'Shambhu Garjana',
    'Shiv Shakti',
    'Shiv Garjana',
    'Rudra Dance',
    'ShankhNaad',
    'Folk Taal (Lezim, Dhwaj, Zaanz)',
    'Janta Raja',
    'Management'
  ];

  const [formData, setFormData] = useState({
    name: '',
    role: '',
    category: 'Shambhu Garjana',
    categories: ['Shambhu Garjana'],
    bio: '',
    photo: '',
    isActive: true,
    order: 0
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('team/admin');
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      } else {
        throw new Error('Failed to fetch team members');
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      category: 'Shambhu Garjana',
      categories: ['Shambhu Garjana'],
      bio: '',
      photo: '',
      isActive: true,
      order: members.length
    });
    setSelectedMember(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;

    if (!isImageFile(file)) {
      toast.error('Please select a valid image file (PNG, JPG, HEIC, WEBP)');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Image size must be less than 50MB');
      return;
    }

    try {
      setUploadingImage(true);
      console.log('🔄 Uploading team member photo:', file.name);
      
      const result = await uploadMediaFile(file, 'team');
      const photoUrl = result.fileUrl || result.url || result.imageUrl;

      setFormData(prev => ({ ...prev, photo: photoUrl }));
      toast.success('Member photo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(`Photo upload failed: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateMember = async () => {
    if (!formData.name.trim()) {
      toast.error('Member name is required');
      return;
    }

    try {
      const response = await api.post('team', formData);
      if (response.ok) {
        toast.success('Team member created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchMembers();
      } else {
        throw new Error('Failed to create member');
      }
    } catch (error) {
      console.error('Error creating member:', error);
      toast.error('Failed to create team member');
    }
  };

  const handleEditMember = async () => {
    if (!selectedMember || !formData.name.trim()) {
      toast.error('Member name is required');
      return;
    }

    try {
      const response = await api.put(`team/${selectedMember.id}`, formData);
      if (response.ok) {
        toast.success('Team member updated successfully!');
        setShowEditModal(false);
        resetForm();
        fetchMembers();
      } else {
        throw new Error('Failed to update member');
      }
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('Failed to update team member');
    }
  };

  const handleDeleteMember = async () => {
    if (!selectedMember) return;

    try {
      const response = await api.delete(`team/${selectedMember.id}`);
      if (response.ok) {
        toast.success('Team member deleted successfully!');
        setShowDeleteModal(false);
        setSelectedMember(null);
        fetchMembers();
      } else {
        throw new Error('Failed to delete member');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    const memberCats = Array.isArray(member.categories) && member.categories.length > 0
      ? member.categories
      : (member.category ? [member.category] : ['Shambhu Garjana']);

    setFormData({
      name: member.name || '',
      role: member.role || '',
      category: memberCats[0] || 'Shambhu Garjana',
      categories: memberCats,
      bio: member.bio || '',
      photo: member.photo || '',
      isActive: member.isActive !== false,
      order: member.order || 0
    });
    setShowEditModal(true);
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchQuery ||
                          member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          member.role?.toLowerCase().includes(searchQuery.toLowerCase());

    const memberCats = Array.isArray(member.categories) && member.categories.length > 0
      ? member.categories
      : [member.category || 'Shambhu Garjana'];

    const matchesCategory = filterCategory === 'all' || memberCats.includes(filterCategory);
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-red-600 rounded-2xl p-6 sm:p-8 text-white shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-semibold backdrop-blur-sm mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Performers & Cultural Teams</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold">Team Members Management</h2>
          <p className="text-orange-100 text-sm mt-1 max-w-xl">
            Manage Dhol Tasha, Lezim, Dhwaj, and Dance performers across Boys, Girls, and Kids sections.
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="inline-flex items-center gap-2 px-5 py-3 bg-white text-orange-600 hover:bg-orange-50 font-bold rounded-xl shadow-md transition-all duration-200 transform hover:-translate-y-0.5"
        >
          <Plus className="w-5 h-5" />
          <span>Add Team Member</span>
        </button>
      </div>

      {/* Filters & Search Bar */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search team member..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">All Categories</option>
            {availableCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Team Member Cards Grid */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading team members...</p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-gray-800">No Team Members Found</h3>
          <p className="text-gray-500 text-sm mt-1">Add your first team member or change your filter options.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMembers.map((member) => (
            <div
              key={member.id}
              className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col items-center text-center relative group"
            >
              {/* Status Badge */}
              <span className={`absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                member.isActive !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {member.isActive !== false ? 'Active' : 'Inactive'}
              </span>

              {/* Circle Avatar */}
              <div className="w-24 h-24 rounded-full p-1 bg-gradient-to-tr from-amber-400 via-orange-500 to-red-500 shadow-md mb-3 flex-shrink-0">
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-full h-full rounded-full object-cover bg-white"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-extrabold text-2xl">
                    {member.name ? member.name.charAt(0) : 'T'}
                  </div>
                )}
              </div>

              {/* Member Details */}
              <h3 className="font-bold text-gray-900 text-base line-clamp-1">{member.name}</h3>
              <p className="text-xs text-orange-600 font-semibold mt-0.5 line-clamp-1">{member.role || member.category}</p>

              {/* Category Badges */}
              <div className="flex items-center gap-1.5 mt-2 flex-wrap justify-center">
                {(Array.isArray(member.categories) && member.categories.length > 0 ? member.categories : [member.category || 'Shambhu Garjana']).map(cat => (
                  <span key={cat} className="px-2.5 py-0.5 bg-orange-50 text-orange-700 rounded-full text-[11px] font-bold">
                    {cat}
                  </span>
                ))}
              </div>
              
              {/* Actions */}
              <div className="mt-4 pt-3 border-t border-gray-100 w-full flex items-center justify-center gap-2">
                <button
                  onClick={() => openEditModal(member)}
                  className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                  title="Edit Member"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setSelectedMember(member); setShowDeleteModal(true); }}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Member"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-gray-100 pb-4">
              <h3 className="text-xl font-bold text-gray-900">
                {showCreateModal ? 'Add New Team Member' : 'Edit Team Member'}
              </h3>
              <button
                onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Photo Avatar Upload */}
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-28 h-28 rounded-full p-1 bg-gradient-to-tr from-amber-400 to-orange-500 shadow-md relative group overflow-hidden">
                  {formData.photo ? (
                    <img src={formData.photo} alt="Avatar preview" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gray-100 flex flex-col items-center justify-center text-gray-400">
                      <ImageIcon className="w-8 h-8 mb-1" />
                      <span className="text-[10px]">No Photo</span>
                    </div>
                  )}
                </div>

                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 font-bold rounded-xl text-xs transition-colors">
                  <Upload className="w-4 h-4" />
                  <span>{uploadingImage ? 'Uploading...' : 'Upload Circle Photo'}</span>
                  <input type="file" accept="image/*,.heic,.heif,image/heic,image/heif" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Member Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Rahul Sharma"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Role / Performance Focus</label>
                <input
                  type="text"
                  placeholder="e.g. Lead Dhol Tasha Player, Lezim Captain"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none"
                />
              </div>

              {/* Category Sections (Multi-select Checkboxes) */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">
                  Category Sections (Select all teams this member belongs to)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-gray-50 p-3 rounded-2xl border border-gray-200">
                  {availableCategories.map(cat => {
                    const isChecked = (formData.categories || []).includes(cat);
                    return (
                      <label
                        key={cat}
                        className={`flex items-center gap-2 p-2 rounded-xl cursor-pointer text-xs font-bold transition-all border select-none ${
                          isChecked
                            ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white border-orange-600 shadow-sm scale-[1.02]'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const current = formData.categories || [];
                            let updated;
                            if (e.target.checked) {
                              updated = [...current, cat];
                            } else {
                              updated = current.filter(c => c !== cat);
                            }
                            setFormData(prev => ({
                              ...prev,
                              categories: updated,
                              category: updated[0] || 'Shambhu Garjana'
                            }));
                          }}
                          className="hidden"
                        />
                        <span className={`w-4 h-4 rounded-md border flex items-center justify-center flex-shrink-0 text-[10px] ${
                          isChecked ? 'border-white bg-white/20' : 'border-gray-300 bg-gray-50'
                        }`}>
                          {isChecked ? '✓' : ''}
                        </span>
                        <span>{cat}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Bio / Quote (Optional)</label>
                <textarea
                  rows="3"
                  placeholder="Short bio or personal quote..."
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none"
                />
              </div>

              {/* Active Toggle & Order */}
              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                  />
                  <span className="text-xs font-bold text-gray-700">Display Active on Website</span>
                </label>

                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-gray-700">Display Order:</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: Number(e.target.value) }))}
                    className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center"
                  />
                </div>
              </div>
            </div>

            {/* Modal Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }}
                className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl text-sm"
              >
                Cancel
              </button>
              <button
                onClick={showCreateModal ? handleCreateMember : handleEditMember}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-xl text-sm shadow-md hover:opacity-90"
              >
                {showCreateModal ? 'Save Member' : 'Update Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedMember && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
              <Trash2 className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Delete Team Member?</h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to remove <strong className="text-gray-900">{selectedMember.name}</strong> from the team?
            </p>
            <div className="flex items-center justify-center gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedMember(null); }}
                className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteMember}
                className="px-6 py-2.5 bg-red-600 text-white font-bold rounded-xl text-sm shadow-md hover:bg-red-700"
              >
                Delete Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
